import React, { useEffect, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import SubmitIcon from 'assets/actionPlan/Dashboard/check.svg';
import comment from 'assets/actionPlan/comment.svg';
import EditIcon from 'assets/actionPlan/edit.svg';
import historyIcon from 'assets/actionPlan/historyIcon.svg';
import humanIcon from 'assets/actionPlan/humanIcon.svg';
import VectorIcon from 'assets/actionPlan/vector.svg';
import Button from 'common-ui/button';
import Input from 'common-ui/input';
import Modal from 'common-ui/modal';
import TextArea from 'common-ui/textArea';
import dayjs from 'dayjs';
import Tag from 'pages/components/tag';
import * as Yup from 'yup';

import {
  actualRecord,
  approveRecord,
  getRecordHistory,
  reviewRecord,
  submitRecord,
} from '../../../../features/actions/actionPlanActions';
import { selectDepartmentsList } from '../../../../features/actions/actionPlanSlice';
import { selectUserInfo } from '../../../../features/auth/authSlice';
import EditPresValue from '../editPresValue/EditPresValue';
import {
  AlertIcon,
  ButtonWrapper,
  DualInputWrapper,
  ErrorForInputs,
  ErrorMessage,
  FormWrapper,
  InputSection,
} from '../editPresValue/EditPresValue.styles';
import {
  ActionButtons,
  ActionName,
  AnalysisLabel,
  AnalysisValue,
  ColumnHeader,
  ColumnSection,
  CommentFormGroup,
  CommentInput,
  CommentLabel,
  CommentTextarea,
  DualColumnWrapper,
  EmployeeName,
  FakeContent,
  FloatingScrollWrapper,
  HistoryComment,
  HistoryContent,
  HistoryDate,
  HistoryEmpty,
  HistoryField,
  HistoryFieldName,
  HistoryHeader,
  HistoryIconCircle,
  HistoryIconText,
  HistoryItem,
  HistoryLine,
  HistoryLoading,
  HistoryModalHeader,
  HistoryModalHeaderIcon,
  HistoryModalTitle,
  HistoryTimeline,
  HistoryTitle,
  HistoryValue,
  IconButton,
  KPIStatus,
  MonthlyAnalysis,
  ScrollBlock,
  SelectDep,
  StatusBadge,
  StatusTooltipAvatar,
  StatusTooltipBox,
  StatusTooltipContent,
  StatusTooltipName,
  StatusTooltipTime,
  StatusTooltipTrigger,
  StatusTooltipWrapper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TypeBadge,
  WeekCell,
  WeekLabel,
  WeekRow,
  WeekValue,
} from './DashboardTable.styles';
import { canEditPlanned, getUserRole } from './dashboardPermissions';

const DashboardTable = ({ data, viewMode, onRefresh, searchParams, filters }) => {
  const dispatch = useDispatch();
  const tableRef = useRef(null);
  const fakeScrollRef = useRef(null);
  const validationSchema = Yup.object({
    quantity: Yup.string().required('Please Entered value'),
    financial: Yup.string(),
  });
  const [modalErrors, setModalErrors] = useState({});

  const [dimensions, setDimensions] = useState({
    width: 0,
    scrollWidth: 0,
    left: 0,
  });
  const userInfo = useSelector(selectUserInfo);
  const allDepartments = useSelector(selectDepartmentsList) || [];
  const tableData = {
    rows: Array.isArray(data?.rows) ? data.rows : [],
  };

  const updateDimensions = () => {
    if (tableRef.current) {
      const rect = tableRef.current.getBoundingClientRect();
      setDimensions({
        width: tableRef.current.offsetWidth,
        scrollWidth: tableRef.current.scrollWidth,
        left: rect.left,
      });
    }
  };

  const [historyModal, setHistoryModal] = useState({
    open: false,
    recordId: null,
    loading: false,
    data: [],
  });

  const visibleRows = [...(tableData.rows ?? [])]
    .filter((row) => {
      if (filters.tabMode !== 'DEPARTMENT') return true;

      return row.cells?.some(
        (cell) => !dayjs(cell.periodStart).startOf('day').isAfter(dayjs().startOf('day'))
      );
    })
    .sort((a, b) => {
      return dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf();
    });

  const [commentModal, setCommentModal] = useState({
    open: false,
    type: null,
    rowIndex: null,
    cellIndex: null,
  });

  const [modalValues, setModalValues] = useState({
    quantity: '',
    financial: '',
    comment: '',
  });

  const isDepartmentMode = filters.tabMode === 'DEPARTMENT';

  const currentModalRow =
    commentModal.rowIndex !== null ? visibleRows[commentModal.rowIndex] : null;

  const isBoth = currentModalRow?.estimationType === 'BOTH';
  const singleValueField =
    currentModalRow?.estimationType === 'FINANCIAL' ? 'financial' : 'quantity';
  const hasValue = (value) =>
    value !== undefined && value !== null && String(value).trim() !== '';

  const formatHistoryNumber = (value) => {
    if (!hasValue(value)) return '';

    const numberValue = Number(value);

    if (Number.isNaN(numberValue)) return value;

    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 2,
    }).format(numberValue);
  };

  const formatHistoryValue = (fieldName, value) => {
    const numericFields = [
      'PRESENTED_QUANTITY',
      'PRESENTED_FINANCIAL',
      'ACTUAL_QUANTITY',
      'ACTUAL_FINANCIAL',
    ];

    if (numericFields.includes(fieldName)) {
      return formatHistoryNumber(value);
    }

    return value;
  };

  const formatKpiStatus = (status) => {
    switch (status) {
      case 'INSUFFICIENT_DATA':
        return 'No Data';
      case 'INSUFFICIENT_DATA / INSUFFICIENT_DATA':
        return 'No Data / No Data';
      case 'BELOW_LOW':
        return 'Below Low';
      case 'BELOW_LOW / BELOW_LOW':
        return 'Below Low / Below Low';
      case 'LOW_TO_TARGET':
        return 'Below Target';
      case 'LOW_TO_TARGET / LOW_TO_TARGET':
        return 'Below Target/ Below Target';
      case 'ON_TARGET':
        return 'On Target';
      case 'ON_TARGET /ON_TARGET':
        return 'On Target / On Target';
      case 'ABOVE_HIGH':
        return 'Above Target';
      case 'ABOVE_HIGH / ABOVE_HIGH':
        return 'Above Target / Above Target';
      default:
        return status || 'No Kpi';
    }
  };

  useEffect(() => {
    if (!commentModal.open) return;

    setModalValues({
      quantity: '',
      financial: '',
      comment: '',
    });

    setModalErrors({});
  }, [commentModal.open]);

  useEffect(() => {
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [data, viewMode]);
  const handleFakeScroll = () => {
    if (fakeScrollRef.current && tableRef.current) {
      tableRef.current.scrollLeft = fakeScrollRef.current.scrollLeft;
    }
  };

  const handleTableScroll = () => {
    if (fakeScrollRef.current && tableRef.current) {
      fakeScrollRef.current.scrollLeft = tableRef.current.scrollLeft;
    }
  };

  const getDepartmentName = (departmentId) => {
    const dept = allDepartments.find((d) => d.id === departmentId);
    return dept?.name || '';
  };

  const userType = userInfo?.userType;
  const userRole = getUserRole(userInfo);

  const isGeneralManager = userType === 'General Manager';
  const isSuperAdmin = userType === 'Super Admin';
  const canEnterActualByRole = isGeneralManager || isSuperAdmin;
  const isEmployee = userType === 'Team Member';

  const currentUserId = userInfo?.uuid || userInfo?.userId;
  const userDepartmentId = userInfo?.departmentId;

  const subordinateIds = userInfo?.subordinateIds || [];
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);
  const normalizeStatus = (status) => (status ? status.toUpperCase() : 'PENDING');
  const getPeriodCells = (cells, from, to) => {
    if (!from || !to) return [];

    const start = dayjs(from).startOf('day');
    const end = dayjs(to).endOf('day');

    return cells.filter((c) => {
      const d = dayjs(c.periodStart);
      return d.isAfter(start) && d.isBefore(end);
    });
  };

  const calculateAverage = (cells, estimationType) => {
    if (!cells.length) return 0;

    let sum = 0;
    let count = 0;

    cells.forEach((c) => {
      if (estimationType === 'FINANCIAL') {
        sum += c.actualFinancial || 0;
      }

      if (estimationType === 'QUANTITY') {
        sum += c.actualQuantity || 0;
      }

      if (estimationType === 'BOTH') {
        sum += (c.actualQuantity || 0) + (c.actualFinancial || 0);
      }

      count++;
    });

    return count ? (sum / count).toFixed(2) : 0;
  };
  const from = searchParams?.from;
  const to = searchParams?.to;

  const [valueModal, setValueModal] = useState({
    open: false,
    mode: null,
    recordId: null,
    estimationType: null,
    quantity: '',
    financial: '',
    comment: '',
  });
  useEffect(() => {
    setIsEditing(false);
    setEditedData({});
  }, [data, viewMode]);

  const getCellPeriodType = (cell) => {
    if (!cell.periodStart || !cell.periodEnd) {
      return 'DAILY';
    }

    const start = dayjs(cell.periodStart).startOf('day');
    const end = dayjs(cell.periodEnd).startOf('day');

    const diff = end.diff(start, 'day') + 1;

    if (diff <= 1) return 'DAILY';
    if (diff <= 7) return 'WEEKLY';

    return 'MONTHLY';
  };

  const columnHeaders = React.useMemo(() => {
    const activePeriodsMap = new Map();

    tableData.rows.forEach((row) => {
      row.cells.forEach((cell) => {
        const cellType = getCellPeriodType(cell);

        if (cellType !== viewMode) {
          return;
        }
        if (cell.periodStart) {
          const dateStr = dayjs(cell.periodStart).format('YYYY-MM-DD');

          if (viewMode === 'DAILY') {
            if (!activePeriodsMap.has(dateStr)) {
              activePeriodsMap.set(dateStr, { date: dateStr });
            }
          } else if (viewMode === 'WEEKLY') {
            const weekKey = `${dayjs(cell.periodStart).format('YYYY-MM-DD')}_${dayjs(cell.periodEnd || cell.periodStart).format('YYYY-MM-DD')}`;
            if (!activePeriodsMap.has(weekKey)) {
              activePeriodsMap.set(weekKey, {
                rangeStart: dayjs(cell.periodStart).startOf('day').valueOf(),
                rangeEnd: dayjs(cell.periodEnd || cell.periodStart)
                  .endOf('day')
                  .valueOf(),
              });
            }
          } else if (viewMode === 'MONTHLY') {
            const monthKey = dayjs(cell.periodStart).format('YYYY-MM');
            if (!activePeriodsMap.has(monthKey)) {
              activePeriodsMap.set(monthKey, {
                month: dayjs(cell.periodStart).month(),
                year: dayjs(cell.periodStart).year(),
                label: dayjs(cell.periodStart).format('MMM').toUpperCase(),
              });
            }
          }
        }
      });
    });

    const sortedKeys = Array.from(activePeriodsMap.keys()).sort((a, b) => {
      if (viewMode === 'WEEKLY') {
        const aStart = a.split('_')[0];
        const bStart = b.split('_')[0];
        return aStart > bStart ? 1 : -1;
      }
      return a > b ? 1 : -1;
    });

    return sortedKeys
      .map((key, i) => {
        const data = activePeriodsMap.get(key);

        if (viewMode === 'DAILY') {
          return {
            label: dayjs(data.date).format('DD-MM-YYYY'),
            date: data.date,
          };
        }
        if (viewMode === 'WEEKLY') {
          return {
            label: `${dayjs(data.rangeStart).format('DD-MM-YYYY')} - ${dayjs(data.rangeEnd).format('DD-MM-YYYY')}`,
            rangeStart: data.rangeStart,
            rangeEnd: data.rangeEnd,
          };
        }
        if (viewMode === 'MONTHLY') {
          return data;
        }
        return null;
      })
      .filter(Boolean);
  }, [tableData.rows, viewMode]);

  const getPeriodAnalysisValue = (row) => {
    if (row.estimationType === 'QUANTITY') {
      return row.monthlyAnalysisQuantity || 0;
    }

    if (row.estimationType === 'FINANCIAL') {
      return row.monthlyAnalysisFinancial || 0;
    }

    if (row.estimationType === 'BOTH') {
      return `${row.monthlyAnalysisQuantity || 0} / ${row.monthlyAnalysisFinancial || 0}`;
    }

    return 0;
  };

  const getStatusTooltipText = (item) => {
    const who = getHistoryUserName(item) || 'Unknown user';
    const role = getHistoryUserRole(item) || 'Unknown role';
    const when = item.timestamp ? dayjs(item.timestamp).format('MMM DD, YYYY, hh:mm A') : '';

    return `${who} / ${role} changed status to "${item.status}" on ${when}`;
  };

  const ANALYSIS_VISIBILITY = {
    DAILY: { weekly: true, monthly: true },
    WEEKLY: { weekly: false, monthly: true },
    MONTHLY: { weekly: false, monthly: false },
  };
  const hasCustomRange = !!(filters?.from && filters?.to);

  const showWeeklyAnalysis =
    !hasCustomRange && (ANALYSIS_VISIBILITY[viewMode]?.weekly || false);

  const showMonthlyAnalysis =
    !hasCustomRange && (ANALYSIS_VISIBILITY[viewMode]?.monthly || false);

  const showPeriodAnalysis = hasCustomRange;
  const handleOpenHistory = async (recordId) => {
    console.log('HISTORY RECORD ID =>', recordId);
    if (!recordId) return;

    setHistoryModal({
      open: true,
      recordId,
      loading: true,
      data: [],
    });

    try {
      const res = await dispatch(getRecordHistory(recordId)).unwrap();

      setHistoryModal((prev) => ({
        ...prev,
        loading: false,
        data: res || [],
      }));
    } catch (e) {
      setHistoryModal((prev) => ({
        ...prev,
        loading: false,
        data: [],
      }));
    }
  };

  useEffect(() => {}, [columnHeaders, visibleRows]);

  const isFullMonthRange = (from, to) => {
    if (!from || !to) return false;

    const start = dayjs(from);
    const end = dayjs(to);

    return start.date() === 1 && end.date() === end.endOf('month').date();
  };

  const renderDataSection = ({
    type,
    row,
    cell,
    rowIndex,
    cellIndex,
    currentCellData,
    rowEditedData,
    isPresEditable,
    isActEditable,
    cellStatus,
    isDisabledBlock,
  }) => {
    const isQuantity = type === 'quantity';
    const planField = isQuantity ? 'plannedQuantity' : 'plannedFinancial';
    const presField = isQuantity ? 'presentedQuantity' : 'presentedFinancial';
    const actField = isQuantity ? 'actualQuantity' : 'actualFinancial';

    const editedPresValue = rowEditedData[cellIndex]?.[presField];
    const originalPresValue = cell?.[presField];
    const presValue = editedPresValue !== undefined ? editedPresValue : originalPresValue;

    const planValue =
      currentCellData[planField] ??
      currentCellData[isQuantity ? 'plannedFinancial' : 'plannedQuantity'];
    const actValue = currentCellData[actField] ?? '';

    const editedValue = rowEditedData[cellIndex]?.[presField];
    const hasChanges = editedValue !== undefined && editedValue !== '';

    const originalActValue = cell?.[actField] || 0;
    const hasActChanges = rowEditedData[cellIndex]?.[actField] !== undefined;

    const isRealCell = cell?.recordId && !String(cell.recordId).startsWith('empty-');
    const currentRow =
      commentModal.rowIndex !== null ? visibleRows[commentModal.rowIndex] : null;

    const isBoth = currentRow?.estimationType === 'BOTH';

    const openModal = (mode, cell) => {
      setValueModal({
        open: true,
        mode,
        recordId: cell.recordId,
        estimationType: cell.estimationType,
        quantity: '',
        financial: '',
        comment: '',
      });
    };
    const submitPresented = async () => {
      const row = visibleRows[commentModal.rowIndex];
      const cell = row?.cells?.find((c) => c.recordId === valueModal.recordId);

      if (!cell?.canSubmit) return;
      if (row?.employeeId !== userInfo?.uuid) return;
      const payload = {
        comment: valueModal.comment,
      };

      if (valueModal.estimationType === 'FINANCIAL') {
        payload.financial = Number(valueModal.financial);
      }

      if (valueModal.estimationType === 'QUANTITY') {
        payload.quantity = Number(valueModal.quantity);
      }

      if (valueModal.estimationType === 'BOTH') {
        payload.quantity = Number(valueModal.quantity);
        payload.financial = Number(valueModal.financial);
      }

      await dispatch(
        submitRecord({
          id: valueModal.recordId,
          submitData: payload,
        })
      );

      closeModal();
    };

    const submitActual = async () => {
      if (userInfo?.userType !== 'General Manager') return;

      const row = visibleRows[commentModal.rowIndex];
      const cell = row?.cells?.find((c) => c.recordId === valueModal.recordId);

      if (!cell?.canEnterActual) return;
      const payload = {
        comment: valueModal.comment,
      };

      if (valueModal.estimationType === 'FINANCIAL') {
        payload.financial = Number(valueModal.financial);
      }

      if (valueModal.estimationType === 'QUANTITY') {
        payload.quantity = Number(valueModal.quantity);
      }

      if (valueModal.estimationType === 'BOTH') {
        payload.quantity = Number(valueModal.quantity);
        payload.financial = Number(valueModal.financial);
      }

      await dispatch(
        actualRecord({
          id: valueModal.recordId,
          actualData: payload,
        })
      );

      closeModal();
    };

    const closeModal = () => {
      setValueModal({
        open: false,
        mode: null,
        recordId: null,
        estimationType: null,
        quantity: '',
        financial: '',
        comment: '',
      });
    };

    const getPeriodCells = (cells, from, to) => {
      if (!from || !to) return [];

      const start = dayjs(from).startOf('day');
      const end = dayjs(to).endOf('day');

      return cells.filter((c) => {
        const d = dayjs(c.periodStart);
        return d.isAfter(start) && d.isBefore(end);
      });
    };

    const calculateAverage = (cells, estimationType) => {
      if (!cells.length) return 0;

      let sum = 0;
      let count = 0;

      cells.forEach((c) => {
        if (estimationType === 'FINANCIAL') {
          sum += c.actualFinancial || 0;
        }

        if (estimationType === 'QUANTITY') {
          sum += c.actualQuantity || 0;
        }

        if (estimationType === 'BOTH') {
          sum += (c.actualQuantity || 0) + (c.actualFinancial || 0);
        }

        count++;
      });

      return count ? (sum / count).toFixed(2) : 0;
    };

    return (
      <>
        <WeekRow $type="plan">
          <WeekLabel>Plan:</WeekLabel>
          <WeekValue
            $type="plan"
            type="number"
            value={planValue ?? ''}
            placeholder="0"
            disabled={!canEditPlanned(userRole, cellStatus) || !isEditing}
            onChange={(e) => handleInputChange(rowIndex, cellIndex, planField, e.target.value)}
          >
            {planValue || 0}
          </WeekValue>
        </WeekRow>

        {filters.tabMode !== 'DEPARTMENT' && (
          <WeekRow
            onClick={() => {
              if (commentModal.type === 'ACT') {
                const cell = row.cells?.find((c) => c.recordId);

                const status = cell?.status;
                if (!cell?.canSubmit) {
                  return;
                }

                if (!['APPROVED', 'MODIFIED'].includes(status)) {
                  alert('Record must be approved before entering actual values');
                  return;
                }

                if (!isGeneralManager) {
                  alert('Only GM can enter actual values');
                  return;
                }
              }
              if (!cell?.canSubmit) return;
              if (row.employeeId !== userInfo?.uuid) return;

              setCommentModal({
                open: true,
                type: 'PRES',
                rowIndex,
                cellIndex,
                estimationType: row.estimationType,
              });
            }}
          >
            <WeekLabel>Pres:</WeekLabel>
            <WeekValue
              $type="pres"
              type="number"
              value={presValue ?? ''}
              placeholder="0"
              title={cell?.comment || ''}
              disabled={!isPresEditable}
              onChange={(e) =>
                handleInputChange(rowIndex, cellIndex, presField, e.target.value)
              }
              onWheel={(e) => e.target.blur()}
            >
              {presValue || 0}
            </WeekValue>
            {/* <img
              src={comment}
              alt="comment"
              onClick={() => setIsCommentModalOpen(true)}
              style={{ cursor: 'pointer' }}
            /> */}
          </WeekRow>
        )}

        <WeekRow
          onClick={() => {
            if (!canEnterActualByRole || !cell?.canEnterActual) return;

            setCommentModal({
              open: true,
              type: 'ACT',
              rowIndex,
              cellIndex,
              estimationType: row.estimationType,
            });
          }}
          $type="act"
          $isEditable={isActEditable}
        >
          <WeekLabel>Act:</WeekLabel>
          <WeekValue $type="act" type="number" value={actValue} placeholder="0">
            {actValue || 0}
          </WeekValue>
          {/* <img
            src={comment}
            alt="comment"
            onClick={() => setIsCommentModalOpen(true)}
            style={{ cursor: 'pointer' }}
          /> */}
        </WeekRow>

        {hasChanges && isRealCell && cell?.canSubmit && (
          <IconButton
            className="submit-pres"
            onClick={() => handleSubmit(row, cell, cellIndex, rowIndex, type)}
          >
            Submit
          </IconButton>
        )}

        {hasActChanges && isActEditable && isRealCell && (
          <IconButton
            className="submit-pres"
            onClick={() => handleSaveActual(rowIndex, cellIndex, type)}
          >
            Submit
          </IconButton>
        )}

        {filters.tabMode !== 'DEPARTMENT' &&
          !isDisabledBlock &&
          (cell?.autoSubmitted || cell?.autoApproved || cell?.status) && (
            <StatusBadge $status={cellStatus}>{cellStatus}</StatusBadge>
          )}
      </>
    );
  };

  const handleInputChange = (rowIndex, cellIndex, field, value) => {
    setEditedData((prev) => ({
      ...prev,
      [rowIndex]: {
        ...prev[rowIndex],
        [cellIndex]: {
          ...prev[rowIndex]?.[cellIndex],
          [field]: value,
        },
      },
    }));
  };

  const handleSubmit = async (row, targetCell, cellIndex, rowIndex, fieldType = null) => {
    if (!targetCell?.recordId || targetCell.recordId.toString().startsWith('empty-')) {
      console.error('Record ID not found for this cell');
      return;
    }
    const realCell = row.cells.find((c) => c.recordId && c.canSubmit);

    const estimationType = row.estimationType;
    const edited = (editedData?.[rowIndex] || {})[cellIndex] || {};
    const submitData = {
      comment: targetCell.comment || '',
    };

    if (estimationType === 'BOTH') {
      if (fieldType === 'quantity') {
        const quantityValue =
          edited.presentedQuantity !== undefined
            ? edited.presentedQuantity
            : targetCell.presentedQuantity;
        submitData.quantity = quantityValue === '' ? 0 : Number(quantityValue);

        const existingFinancial = targetCell.presentedFinancial || 0;
        submitData.financial = existingFinancial;
      } else if (fieldType === 'financial') {
        const financialValue =
          edited.presentedFinancial !== undefined
            ? edited.presentedFinancial
            : targetCell.presentedFinancial;
        submitData.financial = financialValue === '' ? 0 : Number(financialValue);

        const existingQuantity = targetCell.presentedQuantity || 0;
        submitData.quantity = existingQuantity;
      }
    } else if (estimationType === 'FINANCIAL') {
      const financialValue =
        edited.presentedFinancial !== undefined
          ? edited.presentedFinancial
          : targetCell.presentedFinancial;
      submitData.financial = financialValue === '' ? 0 : Number(financialValue);
    } else {
      const quantityValue =
        edited.presentedQuantity !== undefined
          ? edited.presentedQuantity
          : targetCell.presentedQuantity;
      submitData.quantity = quantityValue === '' ? 0 : Number(quantityValue);
    }

    await dispatch(
      submitRecord({
        id: realCell.recordId,
        submitData,
      })
    );

    setEditedData((prev) => {
      const newState = { ...prev };
      if (newState[rowIndex]?.[cellIndex]) {
        const cellData = { ...newState[rowIndex][cellIndex] };

        if (fieldType === 'financial') {
          delete cellData.presentedFinancial;
        } else if (fieldType === 'quantity') {
          delete cellData.presentedQuantity;
        } else {
          if (estimationType === 'FINANCIAL') {
            delete cellData.presentedFinancial;
          } else {
            delete cellData.presentedQuantity;
          }
        }

        if (Object.keys(cellData).length === 0) {
          const { [cellIndex]: _, ...restCellData } = newState[rowIndex];
          newState[rowIndex] = restCellData;
        } else {
          newState[rowIndex][cellIndex] = cellData;
        }
      }
      return newState;
    });

    if (onRefresh) onRefresh();
  };

  const handleSaveActual = async (rowIndex, cellIndex, fieldType = null) => {
    if (!isSuperAdmin) {
      alert('Only Super Admin can enter actual values');
      return;
    }
    const row = visibleRows[rowIndex];
    const header = columnHeaders[cellIndex];

    const matchingCells = row.cells.filter((c) => {
      const cellType = getCellPeriodType(c);

      if (viewMode === 'DAILY') {
        const cellDateStr = dayjs(c.periodStart).format('YYYY-MM-DD');
        return cellDateStr === header.date;
      }

      if (viewMode === 'WEEKLY') {
        const cellTime = dayjs(c.periodStart).startOf('day').valueOf();
        return cellTime >= header.rangeStart && cellTime <= header.rangeEnd;
      }

      if (viewMode === 'MONTHLY') {
        const d = dayjs(c.periodStart);
        return d.month() === header.month && d.year() === header.year;
      }

      return false;
    });
    const cell =
      matchingCells.length > 0
        ? matchingCells.reduce((acc, c) => ({
            ...acc,
            plannedQuantity: (acc.plannedQuantity || 0) + (c.plannedQuantity || 0),
            plannedFinancial: (acc.plannedFinancial || 0) + (c.plannedFinancial || 0),
            presentedQuantity: (acc.presentedQuantity || 0) + (c.presentedQuantity || 0),
            presentedFinancial: (acc.presentedFinancial || 0) + (c.presentedFinancial || 0),
            actualQuantity: (acc.actualQuantity || 0) + (c.actualQuantity || 0),
            actualFinancial: (acc.actualFinancial || 0) + (c.actualFinancial || 0),
            status: acc.status || c.status,
            recordId: acc.recordId || c.recordId,
          }))
        : null;
    if (!['APPROVED', 'MODIFIED'].includes(cell.status)) {
      console.error('Blocked: record not ready for actual entry');
      return;
    }

    const estimationType = row.estimationType;
    const edited = editedData[rowIndex]?.[cellIndex] || {};

    const actualData = {};

    if (estimationType === 'BOTH') {
      if (fieldType === 'quantity') {
        const quantityValue =
          edited.actualQuantity !== undefined ? edited.actualQuantity : cell.actualQuantity;
        actualData.quantity = quantityValue === '' ? 0 : Number(quantityValue);

        const existingFinancial = cell.actualFinancial || 0;
        actualData.financial = existingFinancial;
      } else if (fieldType === 'financial') {
        const financialValue =
          edited.actualFinancial !== undefined ? edited.actualFinancial : cell.actualFinancial;
        actualData.financial = financialValue === '' ? 0 : Number(financialValue);

        const existingQuantity = cell.actualQuantity || 0;
        actualData.quantity = existingQuantity;
      }
    } else if (estimationType === 'FINANCIAL') {
      const financialValue =
        edited.actualFinancial !== undefined ? edited.actualFinancial : cell.actualFinancial;
      actualData.financial = financialValue === '' ? 0 : Number(financialValue);
    } else {
      const quantityValue =
        edited.actualQuantity !== undefined ? edited.actualQuantity : cell.actualQuantity;
      actualData.quantity = quantityValue === '' ? 0 : Number(quantityValue);
    }

    await dispatch(
      actualRecord({
        id: cell.recordId,
        actualData: actualData,
      })
    );

    setEditedData((prev) => {
      const newState = { ...prev };
      if (newState[rowIndex]?.[cellIndex]) {
        const cellData = { ...newState[rowIndex][cellIndex] };

        if (fieldType === 'financial') {
          delete cellData.actualFinancial;
        } else if (fieldType === 'quantity') {
          delete cellData.actualQuantity;
        } else {
          if (estimationType === 'FINANCIAL') {
            delete cellData.actualFinancial;
          } else {
            delete cellData.actualQuantity;
          }
        }

        if (Object.keys(cellData).length === 0) {
          const { [cellIndex]: _, ...restCellData } = newState[rowIndex];
          newState[rowIndex] = restCellData;
        } else {
          newState[rowIndex][cellIndex] = cellData;
        }
      }
      return newState;
    });

    if (onRefresh) onRefresh();
  };

  const handleApprove = async (recordId) => {
    await dispatch(approveRecord(recordId));
    if (onRefresh) onRefresh();
  };

  const handleOpenEditModal = (rowIndex, cellIndex) => {
    const row = visibleRows[rowIndex];
    const header = columnHeaders[cellIndex];

    const matchingCells = row.cells.filter((c) => {
      const cellDateStr = dayjs(c.periodStart).format('YYYY-MM-DD');

      if (viewMode === 'DAILY') return cellDateStr === header.date;

      if (viewMode === 'WEEKLY') {
        const cellTime = dayjs(c.periodStart).startOf('day').valueOf();
        return cellTime >= header.rangeStart && cellTime <= header.rangeEnd;
      }

      if (viewMode === 'MONTHLY') {
        const d = dayjs(c.periodStart);
        return d.month() === header.month && d.year() === header.year;
      }

      return false;
    });

    const targetCell =
      matchingCells.length > 0
        ? matchingCells.reduce((acc, c) => ({
            ...acc,
            presentedQuantity: (acc.presentedQuantity || 0) + (c.presentedQuantity || 0),
            presentedFinancial: (acc.presentedFinancial || 0) + (c.presentedFinancial || 0),
            actualQuantity: (acc.actualQuantity || 0) + (c.actualQuantity || 0),
            actualFinancial: (acc.actualFinancial || 0) + (c.actualFinancial || 0),
            status: acc.status || c.status,
            recordId: acc.recordId || c.recordId,
            canSubmit: acc.canSubmit || c.canSubmit,
            canEnterActual: acc.canEnterActual || c.canEnterActual,
            canReview: acc.canReview || c.canReview,
          }))
        : null;

    setSelectedCell({
      rowIndex,
      cellIndex,
      recordId: targetCell?.recordId,
      estimationType: row.estimationType,
      quantityValue: targetCell?.presentedQuantity ?? '',
      financialValue: targetCell?.presentedFinancial ?? '',
    });

    setIsEditModalOpen(true);
  };

  const handleModalSubmit = async ({ quantityValue, financialValue, comment }) => {
    if (!selectedCell?.recordId) return;

    const row = visibleRows[selectedCell.rowIndex];
    const body = {
      comment: comment || '',
    };

    if (row.estimationType === 'FINANCIAL') {
      body.financial = Number(financialValue) || 0;
    } else if (row.estimationType === 'BOTH') {
      body.quantity = Number(quantityValue) || 0;
      body.financial = Number(financialValue) || 0;
    } else {
      body.quantity = Number(quantityValue) || 0;
    }

    await dispatch(
      reviewRecord({
        id: selectedCell.recordId,
        reviewData: body,
      })
    );

    setIsEditModalOpen(false);
    if (onRefresh) onRefresh();
  };

  if (filters.tabMode === 'DEPARTMENT' && !filters.departmentId && !filters.actionId) {
    return <SelectDep>Please select a Department or Action to view data</SelectDep>;
  }

  if (!tableData.rows || tableData.rows.length === 0) {
    return <SelectDep>No data available</SelectDep>;
  }

  const formatHistoryStatus = (status = 'Pending') =>
    status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();

  const isAutoHistoryEvent = (eventType) =>
    ['AUTO_SUBMIT', 'AUTO_APPROVE', 'AUTO_APROVE'].includes(eventType);

  const getHistoryEventTitle = (item) => {
    const eventTitles = {
      SUBMIT: 'Submitted',
      MANAGER_EDIT: 'Manager Edit',
      APPROVE: 'Manager Approval',
      ACTUAL_ENTRY: 'GM Actual Entry',
      AUTO_SUBMIT: 'Auto Submitted by System',
      AUTO_APPROVE: 'Auto Approved by System',
    };

    const title = eventTitles[item.eventType] || item.eventType || 'Submitted';

    if (isAutoHistoryEvent(item.eventType)) {
      return title;
    }

    const userName = getHistoryUserName(item);
    const userRole = getHistoryUserRole(item);

    if (userName) {
      return `${title} by ${userName}${userRole ? ` / ${userRole}` : ''}`;
    }

    return title;
  };

  const getHistoryFieldName = (fieldName) => {
    const fieldNames = {
      PRESENTED_QUANTITY: 'Presented Quantity',
      PRESENTED_FINANCIAL: 'Presented Financial',
      ACTUAL_QUANTITY: 'Actual Quantity',
      ACTUAL_FINANCIAL: 'Actual Financial',
      COMMENT: 'Comment',
    };

    return fieldNames[fieldName] || fieldName;
  };

  const getCommentLabelByEventType = (eventType) => {
    const labels = {
      SUBMIT: 'Employee comment',
      MANAGER_EDIT: 'Manager comment',
      APPROVE: 'Manager comment',
      ACTUAL_ENTRY: 'GM comment',
      AUTO_SUBMIT: 'System comment',
      AUTO_APROVE: 'System comment',
    };

    return labels[eventType] || 'Comment';
  };
  const getHistoryUserName = (item) =>
    isAutoHistoryEvent(item.eventType) ? 'System' : item.userName;

  const getHistoryUserRole = (item) =>
    isAutoHistoryEvent(item.eventType) ? 'System' : item.userRole;

  const getHistoryComments = (item) =>
    [
      hasValue(item.employeeComment) && {
        label: 'Employee comment',
        value: item.employeeComment,
      },
      hasValue(item.managerComment) && {
        label: 'Manager comment',
        value: item.managerComment,
      },
      hasValue(item.gmComment) && { label: 'GM comment', value: item.gmComment },
      hasValue(item.comment) && { label: 'Comment', value: item.comment },
    ].filter(Boolean);

  const handleCommentSubmit = async () => {
    const currentRow = visibleRows[commentModal.rowIndex];
    try {
      const schema = Yup.object({
        quantity:
          currentRow?.estimationType === 'QUANTITY' || currentRow?.estimationType === 'BOTH'
            ? Yup.string().required('Please enter value')
            : Yup.string(),

        financial:
          currentRow?.estimationType === 'FINANCIAL' || currentRow?.estimationType === 'BOTH'
            ? Yup.string().required('Please enter value')
            : Yup.string(),

        comment: Yup.string(),
      });

      await schema.validate(modalValues, { abortEarly: false });
      setModalErrors({});
    } catch (err) {
      const errors = {};

      err.inner?.forEach((e) => {
        errors[e.path] = e.message;
      });

      setModalErrors(errors);
      return;
    }

    const row = visibleRows[commentModal.rowIndex];
    const header = columnHeaders[commentModal.cellIndex];

    const cell = row.cells.find((c) => {
      const cellDateStr = dayjs(c.periodStart).format('YYYY-MM-DD');

      if (viewMode === 'DAILY') return cellDateStr === header.date;

      if (viewMode === 'WEEKLY') {
        const cellTime = dayjs(c.periodStart).startOf('day').valueOf();
        return cellTime >= header.rangeStart && cellTime <= header.rangeEnd;
      }

      if (viewMode === 'MONTHLY') {
        const d = dayjs(c.periodStart);
        return d.month() === header.month && d.year() === header.year;
      }

      return false;
    });
    const estimationType = currentRow?.estimationType;

    let payload = {
      comment: modalValues.comment,
    };

    if (estimationType === 'FINANCIAL') {
      payload.financial = Number(modalValues.financial) || 0;
    }

    if (estimationType === 'QUANTITY') {
      payload.quantity = Number(modalValues.quantity) || 0;
    }

    if (estimationType === 'BOTH') {
      payload.quantity = Number(modalValues.quantity) || 0;
      payload.financial = Number(modalValues.financial) || 0;
    }

    const action = commentModal.type === 'PRES' ? submitRecord : actualRecord;

    await dispatch(
      action({
        id: cell.recordId,
        [commentModal.type === 'PRES' ? 'submitData' : 'actualData']: payload,
      })
    ).unwrap();

    setCommentModal({
      open: false,
      type: null,
      rowIndex: null,
      cellIndex: null,
    });

    setModalValues({
      quantity: '',
      financial: '',
      comment: '',
    });

    await onRefresh?.();
  };

  return (
    <>
      <ScrollBlock>
        <TableContainer
          key={`${viewMode}-${filters.tabMode}`}
          ref={tableRef}
          onScroll={handleTableScroll}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>ACTION NAME</TableHeader>
                <TableHeader>ACTION TYPE</TableHeader>
                {filters.tabMode === 'EMPLOYEE' && <TableHeader>ESTIMATION TYPE</TableHeader>}
                {filters.tabMode === 'DEPARTMENT' && (
                  <>
                    <TableHeader>DEPARTMENT</TableHeader>
                    <TableHeader>ESTIMATION TYPE</TableHeader>
                  </>
                )}
                {filters.tabMode !== 'DEPARTMENT' &&
                  [
                    'General Manager',
                    'Team Lead',
                    'Super Admin',
                    'Department Head',
                    'Branch Head',
                  ].includes(userInfo?.userType) &&
                  visibleRows.some(
                    (row) => (row.employeeId || row.userId) !== currentUserId
                  ) && <TableHeader className="sticky-employee">EMPLOYEE</TableHeader>}
                {columnHeaders.map((header, idx) => (
                  <TableHeader key={idx}>{header.label || header}</TableHeader>
                ))}
                {showWeeklyAnalysis && <TableHeader>WEEKLY ANALYSIS</TableHeader>}
                {showMonthlyAnalysis && <TableHeader>MONTHLY ANALYSIS</TableHeader>}
                {showPeriodAnalysis && <TableHeader>PERIOD ANALYSIS</TableHeader>}
                <TableHeader>KPI STATUS</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((row, rowIndex) => {
                const rowEditedData = editedData[rowIndex] || {};
                const periodCells = hasCustomRange
                  ? row.cells.filter((c) => {
                      const cellDate = dayjs(c.periodStart).startOf('day');
                      const start = dayjs(filters.from).startOf('day');
                      const end = dayjs(filters.to).endOf('day');

                      return (
                        cellDate.isSame(start, 'day') ||
                        cellDate.isSame(end, 'day') ||
                        (cellDate.isAfter(start) && cellDate.isBefore(end))
                      );
                    })
                  : [];

                const periodActualQuantity = periodCells.reduce(
                  (sum, c) => sum + (Number(c.actualQuantity) || 0),
                  0
                );

                const periodActualFinancial = periodCells.reduce(
                  (sum, c) => sum + (Number(c.actualFinancial) || 0),
                  0
                );

                const rowEmployeeId = row.employeeId || row.userId;
                const isOwnRecord =
                  rowEmployeeId === currentUserId ||
                  (row.departmentId && row.departmentId === userInfo?.departmentId);

                return (
                  <TableRow key={rowIndex}>
                    <TableCell>
                      <ActionName>{row.actionName}</ActionName>
                    </TableCell>
                    <TableCell>
                      <TypeBadge $isPrimary={row.actionType === 'PRIMARY'}>
                        {row.actionType}
                      </TypeBadge>
                    </TableCell>
                    {filters.tabMode === 'EMPLOYEE' && (
                      <TableCell>
                        <ActionName>{row.estimationType}</ActionName>
                      </TableCell>
                    )}
                    {filters.tabMode === 'DEPARTMENT' && (
                      <>
                        <TableCell>
                          <EmployeeName>{getDepartmentName(row.departmentId)}</EmployeeName>
                        </TableCell>

                        <TableCell>
                          <ActionName>{row.estimationType}</ActionName>
                        </TableCell>
                      </>
                    )}
                    {filters.tabMode !== 'DEPARTMENT' &&
                      [
                        'General Manager',
                        'Team Lead',
                        'Super Admin',
                        'Department Head',
                        'Branch Head',
                      ].includes(userInfo?.userType) &&
                      visibleRows.some(
                        (row) => (row.employeeId || row.userId) !== currentUserId
                      ) && (
                        <TableCell className="sticky-employee">
                          <EmployeeName>{row.employeeName}</EmployeeName>
                        </TableCell>
                      )}

                    {columnHeaders.map((header, cellIndex) => {
                      const matchingCells = row.cells.filter((c) => {
                        const cellType = getCellPeriodType(c);

                        if (viewMode === 'DAILY') {
                          return (
                            cellType === 'DAILY' &&
                            dayjs(c.periodStart).format('YYYY-MM-DD') === header.date
                          );
                        }

                        if (viewMode === 'WEEKLY') {
                          const cellTime = dayjs(c.periodStart).startOf('day').valueOf();

                          return (
                            cellType === 'WEEKLY' &&
                            cellTime >= header.rangeStart &&
                            cellTime <= header.rangeEnd
                          );
                        }

                        if (viewMode === 'MONTHLY') {
                          const d = dayjs(c.periodStart);

                          return (
                            cellType === 'MONTHLY' &&
                            d.month() === header.month &&
                            d.year() === header.year
                          );
                        }

                        return false;
                      });

                      const cell =
                        matchingCells.length > 0
                          ? matchingCells.reduce((acc, c) => ({
                              ...acc,
                              plannedQuantity:
                                (acc.plannedQuantity || 0) + (c.plannedQuantity || 0),
                              plannedFinancial:
                                (acc.plannedFinancial || 0) + (c.plannedFinancial || 0),
                              presentedQuantity:
                                (acc.presentedQuantity || 0) + (c.presentedQuantity || 0),
                              presentedFinancial:
                                (acc.presentedFinancial || 0) + (c.presentedFinancial || 0),
                              actualQuantity:
                                (acc.actualQuantity || 0) + (c.actualQuantity || 0),
                              actualFinancial:
                                (acc.actualFinancial || 0) + (c.actualFinancial || 0),
                              status: acc.status || c.status,
                              recordId: acc.recordId || c.recordId,
                            }))
                          : {
                              status: 'PENDING',
                              recordId: `empty-${rowIndex}-${cellIndex}`,
                              plannedQuantity: 0,
                              presentedQuantity: 0,
                            };
                      const isCellEmpty =
                        !cell.recordId || String(cell.recordId).startsWith('empty-');
                      const hasNoData =
                        (cell.plannedQuantity || 0) === 0 &&
                        (cell.presentedQuantity || 0) === 0;
                      const isDisabledBlock = isCellEmpty && hasNoData;
                      const currentCellData = rowEditedData[cellIndex]
                        ? { ...cell, ...rowEditedData[cellIndex] }
                        : cell;

                      const cellStatus = normalizeStatus(cell.status);

                      const currentPresValue =
                        row.estimationType === 'FINANCIAL'
                          ? cell?.presentedFinancial
                          : cell?.presentedQuantity;
                      const isPresEditable = cell.canSubmit;
                      const isActEditable = cell.canEnterActual;
                      return (
                        <TableCell
                          key={cell?.recordId || `${rowIndex}-${cellIndex}`}
                          $hasActions={
                            !isOwnRecord &&
                            !isEmployee &&
                            cellStatus === 'PENDING' &&
                            cell?.recordId &&
                            currentPresValue != null
                          }
                        >
                          <WeekCell $isDisabled={isDisabledBlock}>
                            {row.estimationType === 'BOTH' ? (
                              <DualColumnWrapper>
                                <ColumnSection>
                                  <ColumnHeader>Quantity</ColumnHeader>
                                  {renderDataSection({
                                    type: 'quantity',
                                    row,
                                    cell,
                                    rowIndex,
                                    cellIndex,
                                    currentCellData,
                                    rowEditedData,
                                    isPresEditable,
                                    isActEditable,
                                    cellStatus,
                                    isDisabledBlock,
                                  })}
                                </ColumnSection>

                                <ColumnSection>
                                  <ColumnHeader>Financial</ColumnHeader>
                                  {renderDataSection({
                                    type: 'financial',
                                    row,
                                    cell,
                                    rowIndex,
                                    cellIndex,
                                    currentCellData,
                                    rowEditedData,
                                    isPresEditable,
                                    isActEditable,
                                    cellStatus,
                                    isDisabledBlock,
                                  })}
                                </ColumnSection>
                              </DualColumnWrapper>
                            ) : (
                              <>
                                {renderDataSection({
                                  type:
                                    row.estimationType === 'FINANCIAL'
                                      ? 'financial'
                                      : 'quantity',
                                  row,
                                  cell,
                                  rowIndex,
                                  cellIndex,
                                  currentCellData,
                                  rowEditedData,
                                  isPresEditable,
                                  isActEditable,
                                  cellStatus,
                                  isDisabledBlock,
                                })}
                              </>
                            )}

                            {filters.tabMode !== 'DEPARTMENT' &&
                              !isDisabledBlock &&
                              cell?.recordId && (
                                <IconButton
                                  onClick={() => handleOpenHistory(cell.recordId)}
                                  title="History"
                                  style={{ marginLeft: 'auto' }}
                                >
                                  <img src={historyIcon} alt="history" />
                                </IconButton>
                              )}

                            {cell.canReview && !isDisabledBlock && cell?.recordId && (
                              <ActionButtons>
                                <IconButton
                                  onClick={() => handleApprove(cell.recordId)}
                                  title="Approve"
                                >
                                  <img src={SubmitIcon} alt="approve" />
                                </IconButton>

                                <IconButton
                                  onClick={() => handleOpenEditModal(rowIndex, cellIndex)}
                                  title="Review/Edit"
                                >
                                  <img src={EditIcon} alt="review" />
                                </IconButton>
                              </ActionButtons>
                            )}
                          </WeekCell>
                        </TableCell>
                      );
                    })}

                    {/* {showWeeklyAnalysis && (
                      <TableCell>
                        <MonthlyAnalysis>
                          <AnalysisValue>
                            {row.estimationType === 'BOTH' ? (
                              <>
                                <div>{row.weeklyAnalysisQuantity || 0}</div>
                                <div>{row.weeklyAnalysisFinancial || 0}</div>
                              </>
                            ) : row.estimationType === 'FINANCIAL' ? (
                              row.weeklyAnalysisFinancial || 0
                            ) : (
                              row.weeklyAnalysisQuantity || 0
                            )}
                          </AnalysisValue>
                          <AnalysisLabel>Current Week</AnalysisLabel>
                        </MonthlyAnalysis>
                      </TableCell>
                    )} */}
                    {showWeeklyAnalysis && (
                      <TableCell>
                        <MonthlyAnalysis>
                          <AnalysisValue>
                            {row.estimationType === 'BOTH' ? (
                              <>
                                <div>{row.weeklyAnalysisQuantity ?? 0}</div>
                                <div>{row.weeklyAnalysisFinancial ?? 0}</div>
                              </>
                            ) : row.estimationType === 'FINANCIAL' ? (
                              (row.weeklyAnalysisFinancial ?? 0)
                            ) : (
                              (row.weeklyAnalysisQuantity ?? 0)
                            )}
                          </AnalysisValue>
                          <AnalysisLabel>Current Week</AnalysisLabel>
                        </MonthlyAnalysis>
                      </TableCell>
                    )}

                    {showMonthlyAnalysis && (
                      <TableCell>
                        <MonthlyAnalysis>
                          <AnalysisValue>
                            {row.estimationType === 'BOTH' ? (
                              <>
                                <div>
                                  {filters.tabMode === 'DEPARTMENT'
                                    ? row.totalActualQuantity || 0
                                    : row.monthlyAnalysisQuantity || 0}
                                </div>

                                <div>
                                  {filters.tabMode === 'DEPARTMENT'
                                    ? row.totalActualFinancial || 0
                                    : row.monthlyAnalysisFinancial || 0}
                                </div>
                              </>
                            ) : row.estimationType === 'FINANCIAL' ? (
                              filters.tabMode === 'DEPARTMENT' ? (
                                row.totalActualFinancial || 0
                              ) : (
                                row.monthlyAnalysisFinancial || 0
                              )
                            ) : filters.tabMode === 'DEPARTMENT' ? (
                              row.totalActualQuantity || 0
                            ) : (
                              row.monthlyAnalysisQuantity || 0
                            )}
                          </AnalysisValue>
                          <AnalysisLabel>Total actual</AnalysisLabel>
                        </MonthlyAnalysis>
                      </TableCell>
                    )}

                    {showPeriodAnalysis && (
                      <TableCell>
                        <MonthlyAnalysis>
                          <AnalysisValue>
                            {row.estimationType === 'FINANCIAL' ? (
                              periodActualFinancial
                            ) : row.estimationType === 'QUANTITY' ? (
                              periodActualQuantity
                            ) : (
                              <>
                                <div>{periodActualQuantity}</div>
                                <div>{periodActualFinancial}</div>
                              </>
                            )}
                          </AnalysisValue>
                          <AnalysisLabel>Period Analysis</AnalysisLabel>
                        </MonthlyAnalysis>
                      </TableCell>
                    )}
                    <TableCell>
                      <KPIStatus $status={row.kpiStatus}>
                        {formatKpiStatus(row.kpiStatus)}
                      </KPIStatus>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <EditPresValue
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            estimationType={selectedCell?.estimationType}
            initialQuantityValue={selectedCell?.quantityValue ?? ''}
            initialFinancialValue={selectedCell?.financialValue ?? ''}
            onSubmit={handleModalSubmit}
            loading={false}
          />
          <Modal
            isOpen={commentModal.open}
            onClose={() => {
              setCommentModal({
                open: false,
                type: null,
                rowIndex: null,
                cellIndex: null,
              });

              setModalErrors({});
            }}
            title={commentModal.type === 'PRES' ? 'Add Presented Value' : 'Add Actual Value'}
            width="448px"
            $centered
            footer={false}
          >
            <FormWrapper>
              {isBoth ? (
                <DualInputWrapper>
                  <InputSection className="dashboard-value-input">
                    <Input
                      label="Quantity"
                      required
                      placeholder="Fill in the quantity"
                      value={modalValues.quantity}
                      onChange={(e) => {
                        setModalValues((prev) => ({
                          ...prev,
                          quantity: e.target.value,
                        }));
                        setModalErrors((prev) => ({ ...prev, quantity: '' }));
                      }}
                      error={!!modalErrors.quantity}
                      fullWidth
                    />

                    {modalErrors.quantity && (
                      <ErrorForInputs>
                        <AlertIcon src={VectorIcon} />
                        <ErrorMessage>{modalErrors.quantity}</ErrorMessage>
                      </ErrorForInputs>
                    )}
                  </InputSection>

                  <InputSection className="dashboard-value-input">
                    <Input
                      label="Financial"
                      required
                      placeholder="Fill in the financial"
                      value={modalValues.financial}
                      onChange={(e) => {
                        setModalValues((prev) => ({
                          ...prev,
                          financial: e.target.value,
                        }));
                        setModalErrors((prev) => ({ ...prev, financial: '' }));
                      }}
                      error={!!modalErrors.financial}
                      fullWidth
                    />

                    {modalErrors.financial && (
                      <ErrorForInputs>
                        <AlertIcon src={VectorIcon} />
                        <ErrorMessage>{modalErrors.financial}</ErrorMessage>
                      </ErrorForInputs>
                    )}
                  </InputSection>
                </DualInputWrapper>
              ) : (
                <InputSection>
                  <Input
                    label={singleValueField === 'financial' ? 'Financial' : 'Quantity'}
                    required
                    placeholder={`Fill in the ${singleValueField} value`}
                    value={modalValues[singleValueField]}
                    onChange={(e) => {
                      setModalValues((prev) => ({
                        ...prev,
                        [singleValueField]: e.target.value,
                      }));

                      setModalErrors((prev) => ({
                        ...prev,
                        [singleValueField]: '',
                      }));
                    }}
                    error={!!modalErrors[singleValueField]}
                    fullWidth
                  />

                  {modalErrors[singleValueField] && (
                    <ErrorForInputs>
                      <AlertIcon src={VectorIcon} />
                      <ErrorMessage>{modalErrors[singleValueField]}</ErrorMessage>
                    </ErrorForInputs>
                  )}
                </InputSection>
              )}

              <TextArea
                label="Comment"
                placeholder="Add comment"
                tooltip="Add comment"
                value={modalValues.comment}
                onChange={(e) =>
                  setModalValues((prev) => ({
                    ...prev,
                    comment: e.target.value,
                  }))
                }
                maxLength={250}
                resizeHorizontal={false}
              />

              <ButtonWrapper>
                <Button
                  outlined
                  onClick={() => {
                    setCommentModal({
                      open: false,
                      type: null,
                      rowIndex: null,
                      cellIndex: null,
                    });
                    setModalErrors({});
                  }}
                >
                  Cancel
                </Button>

                <Button primary onClick={handleCommentSubmit}>
                  Submit
                </Button>
              </ButtonWrapper>
            </FormWrapper>
          </Modal>{' '}
          <Modal
            isOpen={historyModal.open}
            onClose={() =>
              setHistoryModal({
                open: false,
                recordId: null,
                loading: false,
                data: [],
              })
            }
            width="840px"
            footer={false}
            closeIcon
          >
            <HistoryModalHeader>
              <HistoryModalHeaderIcon src={historyIcon} alt="history" />
              <HistoryModalTitle>Activity History</HistoryModalTitle>
            </HistoryModalHeader>

            {historyModal.loading ? (
              <HistoryLoading>Loading...</HistoryLoading>
            ) : (
              <HistoryContent>
                <HistoryTimeline>
                  <HistoryLine />

                  {historyModal.data?.length ? (
                    [...historyModal.data]
                      .sort(
                        (a, b) => dayjs(b.timestamp).valueOf() - dayjs(a.timestamp).valueOf()
                      )
                      .map((item) => {
                        const status = item.status || 'Pending';
                        const formattedStatus = formatHistoryStatus(status);
                        const comments = getHistoryComments(item);
                        const statusChange = item.changes?.find(
                          (c) => c.fieldName === 'STATUS' || c.fieldName === 'status'
                        );

                        const previousStatus = statusChange?.previousValue;
                        const newStatus = statusChange?.newValue || formattedStatus;

                        return (
                          <HistoryItem key={item.id}>
                            <HistoryIconCircle>
                              <img src={humanIcon} alt="user" />
                            </HistoryIconCircle>

                            <HistoryHeader>
                              <HistoryTitle>{getHistoryEventTitle(item)}</HistoryTitle>
                              <StatusTooltipWrapper>
                                <Tag type="status" variant={formattedStatus} />

                                <StatusTooltipTrigger>
                                  {(getHistoryUserName(item) || 'U').charAt(0)}
                                </StatusTooltipTrigger>

                                <StatusTooltipBox>
                                  <StatusTooltipAvatar>
                                    {(getHistoryUserName(item) || 'U').charAt(0)}
                                  </StatusTooltipAvatar>

                                  <StatusTooltipContent>
                                    <div
                                      style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        marginBottom: '6px',
                                      }}
                                    >
                                      <StatusTooltipName>
                                        {getHistoryUserName(item)}
                                      </StatusTooltipName>

                                      <StatusTooltipTime>
                                        {item.timestamp
                                          ? dayjs(item.timestamp).format(
                                              'MMM DD, YYYY, hh:mm A'
                                            )
                                          : ''}
                                      </StatusTooltipTime>
                                    </div>

                                    <div>
                                      Status changed to <strong>{formattedStatus}</strong>
                                    </div>
                                  </StatusTooltipContent>
                                </StatusTooltipBox>
                              </StatusTooltipWrapper>
                            </HistoryHeader>

                            {item.changes?.length > 0 &&
                              item.changes
                                .filter(
                                  (c) => c.fieldName !== 'COMMENT' || hasValue(c.newValue)
                                )
                                .map((c, i) => (
                                  <HistoryField key={i}>
                                    <HistoryFieldName>
                                      {c.fieldName === 'COMMENT'
                                        ? getCommentLabelByEventType(item.eventType)
                                        : getHistoryFieldName(c.fieldName)}
                                    </HistoryFieldName>
                                    {c.previousValue !== undefined &&
                                      c.previousValue !== null && (
                                        <>
                                          from{' '}
                                          <HistoryValue>
                                            {formatHistoryValue(c.fieldName, c.previousValue)}
                                          </HistoryValue>{' '}
                                          to{' '}
                                        </>
                                      )}
                                    <HistoryValue>
                                      {formatHistoryValue(c.fieldName, c.newValue)}
                                    </HistoryValue>
                                  </HistoryField>
                                ))}

                            <HistoryDate>
                              {dayjs(item.timestamp).format('MMM DD, YYYY, hh:mm A')}
                            </HistoryDate>

                            {comments.map((commentItem, index) => (
                              <HistoryComment key={index}>
                                <strong>{commentItem.label}: </strong>
                                {commentItem.value}
                              </HistoryComment>
                            ))}
                          </HistoryItem>
                        );
                      })
                  ) : (
                    <HistoryEmpty>No history found</HistoryEmpty>
                  )}
                </HistoryTimeline>
              </HistoryContent>
            )}
          </Modal>
        </TableContainer>
        <FloatingScrollWrapper
          ref={fakeScrollRef}
          onScroll={handleFakeScroll}
          $isVisible={dimensions.scrollWidth > dimensions.width}
          $containerWidth={dimensions.width}
          $containerLeft={dimensions.left}
        >
          <FakeContent style={{ width: dimensions.scrollWidth }} />
        </FloatingScrollWrapper>
      </ScrollBlock>
    </>
  );
};

export default DashboardTable;
